import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
// import { motion } from "framer-motion";
import { useQuery /* , useMutation, useQueryClient  */ } from "@tanstack/react-query";
import toast from "react-hot-toast";
import MoonLoader from "react-spinners/MoonLoader";
import { useAuth0 } from "@auth0/auth0-react";
import { isNull /* , uniq, isEmpty */ } from "lodash";
import clsx from "clsx";
import { Helmet } from "react-helmet";

// import GuessPriceModal from "./GuessPriceModal";
import CommentsModal from "./CommentsModal/CommentsModal";
import TagList from "../../../../components/common/TagList";
import SocialPropertyInfo from "./SocialPropertyInfo/SocialPropertyInfo";
import { LIVE_VARIANT_VERSIONS } from "../../../../constants/live-variant-versions";
import { ReactionBar } from "../../../../components/common/reactions";

// import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { PiShareFatFill } from "react-icons/pi";
import { MdComment } from "react-icons/md";
import { IoIosArrowBack } from "react-icons/io";

import useLocalStorage from "../../../../hooks/useLocalStorage";

import {
    getUserComments,
    /* createGuess, */ getUserGuess /* , deleteUserSocialGuess */,
} from "../../../../services/UserService";
import { getListingPropertyGuessStats, getPropertyByLinkCode } from "../../../../services/PropertyService";
import { useUser } from "../../../../context/UserContext";
import config from "../../../../config";
import { triggerShareSocial } from "../../../../helpers/triggerShare";
import { encodeAddressForUrl } from "../../../../helpers/helpers";
// import { GUESS_TYPES } from "../../../../constants/guess-types";
// import useCustomLoginWithRedirect from "../../../../hooks/useCustomLoginWithRedirect";
import HashtagModal from "./HashtagModal";
import SplashModal from "./SplashModal";
import { getPropertyTags } from "../../../../services/TagsService";
import { FIVE_MINUTES, THIRTY_MINUTES } from "../../../../constants/time";
import useMixpanelEvent from "../../../../hooks/useMixpanelEvent";
import useMixpanelPageViewTracking from "../../../../hooks/useMixpanelPageViewTracking";
import { SOCIAL_FEED_TITLE } from "../../../../constants/social-feed-title";
import { getShareTextForProperty } from "../../../../helpers/getShareTextForProperty";
const ITEMS_PER_PAGE = 10;
const STALE_TIME = FIVE_MINUTES; // 5 minutes
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes
const SPLASH_TIMER_DURATION = 4500;

const getInitialSplashState = (isAuthenticated) => {
    // Only show splash screen for unauthenticated users
    if (!isAuthenticated) {
        return !localStorage.getItem("splashSeenGuest");
    }
    return !localStorage.getItem("splashSeenRegistered");
};

/**
 * ListingPage component displays Information about Property with swiper and interaction buttons
 * @returns {JSX.Element} -  Detail information about Property with interaction buttons.
 */
const ListingPage = () => {
    const hideTag = LIVE_VARIANT_VERSIONS.social.hideTag;
    const navigate = useNavigate();
    // const queryClient = useQueryClient();
    const bodyRef = useRef(null); // eslint-disable-next-line
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    // const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const userId = userState?.id; //eslint-disable-next-line
    const [socialState, setSocialState] = useLocalStorage("social-state");
    /* const [guessedSocialProperties, setGuessedSocialProperties] = useLocalStorage(
        "guessed-social-properties",
        []
    ); */
    const [propertyId, setPropertyId] = useState(null);
    const [property, setProperty] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [, /* previousGuess */ setPreviousGuess] = useState(null);
    const [, /* guessType */ setGuessType] = useState(null);
    const [, /* guessStats */ setGuessStats] = useState(null);
    // const [showGuessPriceModal, setShowGuessPriceModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [preferredVersion, setPreferredVersion] = useLocalStorage("live-variant-preferred-version");
    const isSocial = window.location.pathname.includes("/social/");
    const [showHashtagModal, setShowHashtagModal] = useState(false);
    const [showTagCommentsModal, setShowTagCommentsModal] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [visitorStatus] = useLocalStorage("visitor-status");
    const propertyContentRef = useRef(null);
    const { property_link_code } = useParams();
    const [showSplash, setShowSplash] = useState(() => getInitialSplashState(isAuthenticated));
    const [splashTimerDone, setSplashTimerDone] = useState(false);

    useEffect(() => {
        if (socialState?.property?.id) {
            setPropertyId(socialState.property.id);
        } else if (property?.id) {
            setPropertyId(property.id);
        }
    }, [socialState, property]);

    // Use the custom hook for page view tracking
    useMixpanelPageViewTracking({
        targetRef: propertyContentRef,
        property,
        pageName: "Property Details",
        userState,
        visitorStatus,
        propertyUrl: window.location.href,
        threshold: 1.0,
        debounceDelay: 100,
        immediate: true,
    });

    // Query for tags
    const { data: tagsData } = useQuery({
        queryKey: ["propertyTags", propertyId],
        queryFn: () => getPropertyTags(propertyId),
        enabled: !isNull(propertyId),
        staleTime: FIVE_MINUTES,
    });

    const { data: userGuessData } = useQuery({
        queryKey: ["listing-page-user-guess", propertyId, userId],
        queryFn: () => getUserGuess(userId, propertyId, null, null, null, "social"),
        enabled: !isNull(userId) && !isNull(propertyId),
        staleTime: 0,
    });

    useEffect(() => {
        if (userGuessData) {
            const prediction = userGuessData?.guess?.[0]?.prediction || null;
            setPreviousGuess(prediction);
            setGuessType(prediction);
        }
    }, [userGuessData]);

    const { data: propertyData, error: propertyError } = useQuery({
        queryKey: ["live-variant-listingPage-property", property_link_code],
        queryFn: () => getPropertyByLinkCode({ property_link_code }),
        enabled: !!property_link_code,
        cacheTime: 0,
    });

    useEffect(() => {
        if (propertyData) {
            setProperty(propertyData.data);
            setIsLoading(false);
        }
    }, [propertyData]);

    useEffect(() => {
        if (propertyError) {
            if (
                propertyError?.response?.status === 500 ||
                propertyError?.response?.status === 400 ||
                propertyError?.response?.status === 404
            ) {
                toast.error("Property not found, Please try again later!");
            }
        }
    }, [propertyError]);

    const { data: guessStatsData, error: guessStatsError } = useQuery({
        queryKey: ["live-variant-listingPage-property-guessStat", propertyId],
        queryFn: () => getListingPropertyGuessStats({ property_id: propertyId }),
        enabled: !isNull(propertyId),
        cacheTime: 0,
    });

    useEffect(() => {
        if (guessStatsData) {
            setGuessStats(guessStatsData.data.data);
        }
    }, [guessStatsData]);

    useEffect(() => {
        if (guessStatsError) {
            if (guessStatsError?.response?.status === 500 || guessStatsError?.response?.status === 400) {
                toast.error("Property not found, Please try again later!");
            }
        }
    }, [guessStatsError]);

    /* const { mutate: submitGuess } = useMutation({
        mutationFn: ({ userId, propertyId, prediction, guessPrice, guessReason }) =>
            createGuess(userId, propertyId, prediction, guessPrice, guessReason),
        onSuccess: () => {
            setGuessType(null);
            if (!guessedSocialProperties.includes(propertyId)) {
                setGuessedSocialProperties(uniq([...guessedSocialProperties, propertyId]));
            }
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingPage-property-guessStat"],
            });
            toast.success("Guess submitted successfully!");
        },
        onError: (error) => {
            console.error("Error submitting guess:", error);
            toast.error(error.response?.data?.message || "Failed to submit guess");
        },
    }); */

    /* Guess related. Commented out for now */
    /* const { mutate: deleteGuess } = useMutation({
        mutationFn: () => deleteUserSocialGuess(userId, propertyId),
        onSuccess: () => {
            setGuessType(null);
            // Optimistically update all related caches
            queryClient.setQueryData(["user-guess", propertyId, userId], (oldData) => ({
                ...oldData,
                guess: [],
            }));

            // Update listing feed cache
            queryClient.setQueryData(["live-variant-listingFeed-properties"], (oldData) => {
                if (!oldData?.data) return oldData;
                return {
                    ...oldData,
                    data: oldData.data.map((prop) => {
                        if (prop.id === propertyId) {
                            return {
                                ...prop,
                                currentUserGuess: [],
                            };
                        }
                        return prop;
                    }),
                };
            });

            // Update listing page cache
            queryClient.setQueryData(["live-variant-listingPage-property", propertyId], (oldData) => {
                if (!oldData?.data) return oldData;
                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        currentUserGuess: [],
                    },
                };
            });

            // Invalidate all related queries
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingPage-property-guessStat"],
            });
            queryClient.invalidateQueries(["comments"]);
            queryClient.invalidateQueries(["listing-page-user-guess"]);
            queryClient.invalidateQueries(["userProperties"]);
            queryClient.invalidateQueries(["user-guess"]);
            toast.success("Guess deleted successfully!");
        },
        onError: (error) => {
            console.error("Error deleting guess:", error);
            toast.error(error.response?.data?.message || "Failed to delete guess");
        },
    }); */

    /* Guess related. Commented out for now */
    /* const handleGuessClick = (type) => {
        if (!isAuthenticated) {
            if (!isEmpty(property.full_address)) {
                navigate("/social?login=true");
                return;
            }
            customLoginWithRedirect({
                authorizationParams: {
                    redirect_uri:
                        window.location.origin + "/social/" + property.full_address.replace(/\s+/g, "-"),
                },
                appState: {
                    toggleProfile: true,
                    event: {
                        name: "tutorial_register_click",
                        referral_code: localStorage.getItem("referral_code"),
                    },
                },
            });
            return;
        }

        if (type === guessType) {
            deleteGuess();
            return;
        }

        setSocialState({
            ...socialState,
            property,
            guess: {
                prediction: type,
                guessPrice: type === GUESS_TYPES.EXACT ? Number(property.list_price) : 0,
                guessReason: "",
            },
        });
        setGuessType(type);
        setShowCommentModal(true);
    }; */

    /* const getGuessPercentages = (stats) => {
        const { noOfExactGuess = 0, noOfOverGuess = 0, noOfUnderGuess = 0 } = stats || {};
        const total = noOfExactGuess + noOfOverGuess + noOfUnderGuess;

        if (total === 0) return { under: 0, exact: 0, over: 0 };

        return {
            under: Math.round((noOfUnderGuess / total) * 100),
            exact: Math.round((noOfExactGuess / total) * 100),
            over: Math.round((noOfOverGuess / total) * 100),
        };
    }; */

    /* const handleSubmitGuess = (NewGuessPriceAndReason) => {
        setShowGuessPriceModal(false);

        submitGuess({
            userId,
            propertyId: property.id,
            prediction: guessType,
            guessPrice: Number(NewGuessPriceAndReason.price),
            guessReason: NewGuessPriceAndReason.reason,
        });
    }; */

    const { data: commentsData } = useQuery({
        queryKey: ["userComments", userId],
        queryFn: ({ pageParam = 1 }) => getUserComments(userId, pageParam, ITEMS_PER_PAGE),
        enabled: !!userId,
        staleTime: STALE_TIME, // Consider data fresh for 5 minutes
        cacheTime: CACHE_TIME, // Keep data in cache for 30 minutes
    });

    const handleShare = async () => {
        if (!property) return;

        setSocialState({
            ...socialState,
            shareProperty: property.id,
        });

        const shareTextBody = await getShareTextForProperty({ userId, property });
        const shareUrl = `${config.BASE_URL}/${preferredVersion}/listing/${encodeAddressForUrl(property.full_address)}/${property.property_link_code}?utm_medium=share`;

        // Track share event
        const eventData = {
            isAuthenticated: !!userState?.id,
            visitorStatus: visitorStatus || "new",
            pageName: "Property Details",
            propertyUrl: shareUrl,
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.email_address || null,
        };

        sendMixpanelEvent("Share", eventData);

        await triggerShareSocial({
            web: {
                text: `${shareTextBody}\n\n${shareUrl}`,
            },
            mobile: {
                text: shareTextBody,
                url: shareUrl,
            },
        });
    };

    const handleCloseCommentsModal = () => {
        setSocialState({
            ...socialState,
            commentId: null,
            guess: null,
        });
        setShowCommentModal(false);
        setSelectedTag(null);
        // setGuessType(previousGuess);
    };

    const handleTagClick = (tag) => {
        setSelectedTag(tag);
        setShowTagCommentsModal(true);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        if (isSocial) {
            setPreferredVersion("social");
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!showSplash) return;
        const timer = setTimeout(() => setSplashTimerDone(true), SPLASH_TIMER_DURATION);
        return () => clearTimeout(timer);
    }, [showSplash]);

    const handleSplashClose = useCallback(() => {
        if (isAuthenticated) {
            localStorage.setItem("splashSeenRegistered", "true");
        } else {
            localStorage.setItem("splashSeenGuest", "true");
        }
        setShowSplash(false);
    }, [isAuthenticated]);

    useEffect(() => {
        if (showSplash && splashTimerDone && !isLoading) {
            handleSplashClose();
        }
    }, [showSplash, splashTimerDone, isLoading, handleSplashClose]);

    if (showSplash) {
        return <SplashModal onClose={handleSplashClose} />;
    }

    return (
        <>
            <Helmet>
                <title>PriceMe Social | {property?.full_address || SOCIAL_FEED_TITLE}</title>
            </Helmet>
            {isLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-3/4">
                    <MoonLoader size={40} color="#63c19f" />
                </div>
            ) : (
                <div className="property-container tw-relative" ref={bodyRef}>
                    <button
                        className="tw-fixed tw-top-[10svh] tw-left-4 md:tw-left-16 lg:tw-left-[25rem] tw-z-[50] tw-bg-black/50 tw-backdrop-blur-md tw-text-white tw-rounded-full tw-shadow-md tw-transition-all tw-flex tw-items-center tw-justify-center"
                        onClick={() => {
                            localStorage.removeItem(`swiper-index-${propertyId}`);
                            navigate("/social");
                        }}
                    >
                        <IoIosArrowBack className="tw-h-6 tw-w-6 md:tw-h-10 md:tw-w-10 lg:tw-h-7 lg:tw-w-7 tw-text-white tw-cursor-pointer" />
                    </button>
                    <div ref={propertyContentRef}>
                        <SocialPropertyInfo property={property} />
                    </div>
                    <div
                        className={clsx(
                            "tw-sticky tw-bottom-0 tw-flex tw-flex-col tw-justify-center !tw-py-2 !tw-px-3 tw-z-[50] md:tw-w-[610px] tw-min-h-[16svh] tw-bg-white tw-border-t-[1px] tw-border-solid tw-border-gray-400 tw-mx-auto",
                            {
                                "!tw-h-[10svh]": tagsData?.data?.length === 0,
                            }
                        )}
                    >
                        {/* Reactions Row */}
                        <div className="tw-flex tw-justify-left tw-mb-3">
                            <ReactionBar
                                propertyId={property?.id}
                                pageName="Property Details"
                            />
                        </div>
                            {/* Price Guessing Buttons: Commented out for now */}
                            {/* <div
                                className={clsx(
                                    "tw-flex tw-items-center tw-rounded-full tw-bg-gray-300 tw-p-1",
                                    {
                                        "tw-opacity-60 tw-pointer-events-none":
                                            property?.status_indicator === "Closed",
                                        "!tw-bg-[#13C5FC]": guessType === GUESS_TYPES.EXACT,
                                    }
                                )}
                            >
                                <div
                                    className={clsx(
                                        "tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-cursor-pointer",
                                        {
                                            "tw-bg-[#7CC836]": guessType === GUESS_TYPES.HIGHER,
                                        }
                                    )}
                                    onClick={() => handleGuessClick(GUESS_TYPES.HIGHER)}
                                >
                                    <FaArrowUp />
                                </div>
                                <span
                                    onClick={() => handleGuessClick(GUESS_TYPES.EXACT)}
                                    className="tw-font-bold tw-cursor-pointer tw-px-2 tw-py-0.5"
                                >
                                    ${property?.list_price?.toLocaleString("en-US")}
                                </span>
                                <div
                                    className={clsx(
                                        "tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-cursor-pointer",
                                        {
                                            "tw-bg-[#EA4949]": guessType === GUESS_TYPES.LOWER,
                                        }
                                    )}
                                    onClick={() => handleGuessClick(GUESS_TYPES.LOWER)}
                                >
                                    <FaArrowDown />
                                </div>
                            </div> */}
                        {/* Comments, Share, AddTag Row */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full">
                                <button
                                    className={clsx(
                                        "tw-flex tw-items-center tw-justify-center tw-bg-[#F3F4F6] tw-border tw-text-black tw-border-gray-300 tw-rounded-full tw-px-3 tw-py-2 tw-shadow-sm hover:tw-bg-gray-100 tw-transition-colors tw-flex-1",
                                        commentsData?.total === 0 && "pulse"
                                    )}
                                    onClick={() => setShowCommentModal(true)}
                                >
                                    <MdComment className="tw-mr-2 tw-text-lg" />
                                    <span className="tw-font-semibold tw-text-sm">
                                        {property?.commentCount ?? 0} Comments
                                    </span>
                                </button>
                                <button
                                    className="tw-flex tw-items-center tw-justify-center tw-text-black tw-bg-[#F3F4F6] tw-border tw-border-gray-300 tw-rounded-full tw-px-3 tw-py-2 tw-shadow-sm hover:tw-bg-gray-100 tw-transition-colors tw-flex-1"
                                    onClick={handleShare}
                                >
                                    <PiShareFatFill className="tw-mr-2 tw-text-lg" />
                                    <span className="tw-font-semibold tw-text-sm">Share</span>
                                </button>
                                <button
                                    className={clsx(
                                        "tw-bg-[#6C26C4] tw-text-white tw-font-semibold tw-px-4 tw-py-2.5 tw-rounded-full tw-text-xs tw-shadow-md hover:tw-bg-[#4b2fcf] tw-transition-colors",
                                        {
                                            "tw-hidden": hideTag,
                                        }
                                    )}
                                    style={{ minWidth: "fit-content" }}
                                    onClick={() => setShowHashtagModal(true)}
                                >
                                    #AddTag
                                </button>
                        </div>

                        {/* TagList below actions */}
                        {!hideTag && (
                            <TagList
                                tags={tagsData?.data}
                                onTagClick={handleTagClick}
                                selectedTag={selectedTag}
                                propertyId={property?.id || propertyId}
                            />
                        )}

                        {/* Smooth Animated Progress Bar - 3 segments */}
                        {/* <div className="tw-flex tw-flex-col">
                            <div className="tw-w-[100%] tw-bg-gray-300 tw-rounded-full tw-overflow-hidden tw-mt-2 tw-h-4 tw-flex">
                                {(() => {
                                    const { under, exact, over } = getGuessPercentages(guessStats);
                                    return (
                                        <>
                                            <motion.div
                                                className="tw-h-full tw-bg-[#EA4949]"
                                                initial={{ width: "0%" }}
                                                animate={{ width: `${under}%` }}
                                                transition={{ duration: 1.5, ease: "easeInOut" }}
                                            />
                                            <motion.div
                                                className="tw-h-full tw-bg-[#FFC107]"
                                                initial={{ width: "0%" }}
                                                animate={{ width: `${exact}%` }}
                                                transition={{
                                                    duration: 1.5,
                                                    ease: "easeInOut",
                                                    delay: 0.2,
                                                }}
                                            />
                                            <motion.div
                                                className="tw-h-full tw-bg-[#3B166A]"
                                                initial={{ width: "0%" }}
                                                animate={{ width: `${over}%` }}
                                                transition={{
                                                    duration: 1.5,
                                                    ease: "easeInOut",
                                                    delay: 0.4,
                                                }}
                                            />
                                        </>
                                    );
                                })()}
                            </div>

                            <div className="tw-flex tw-justify-between tw-gap-x-2 tw-mt-1 tw-text-sm">
                                <div className="tw-flex tw-items-center">
                                    Under List: {guessStats?.noOfUnderGuess}
                                </div>
                                <div className="tw-flex tw-items-center">
                                    Exact: {guessStats?.noOfExactGuess}
                                </div>
                                <div className="tw-flex tw-items-center">
                                    Over List: {guessStats?.noOfOverGuess}
                                </div>
                            </div>
                        </div> 
                        */}
                    </div>
                </div>
            )}

            {/* Guess Price Modal */}
            {/* {showGuessPriceModal && (
                <GuessPriceModal
                    guess={guessType}
                    show={showGuessPriceModal}
                    handleClose={() => setShowGuessPriceModal(false)}
                    onSubmit={handleSubmitGuess}
                    list_price={property.list_price}
                />
            )} */}

            {(showCommentModal || socialState?.commentId) && (
                <CommentsModal
                    propertyId={property?.id || propertyId}
                    show
                    handleClose={handleCloseCommentsModal}
                    tags={tagsData?.data}
                />
            )}
            <HashtagModal
                userId={userId}
                propertyId={property?.id}
                show={showHashtagModal}
                onClose={() => setShowHashtagModal(false)}
            />
            {showTagCommentsModal && selectedTag && (
                <CommentsModal
                    propertyId={property?.id || propertyId}
                    show={showTagCommentsModal}
                    handleClose={() => {
                        setShowTagCommentsModal(false);
                        setSelectedTag(null);
                    }}
                    tag={selectedTag}
                    tags={tagsData?.data}
                />
            )}
        </>
    );
};

export default ListingPage;
